import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";

// Normalized schema - only essential data stored in DB (no user data duplication)
export interface RealtorCommentRaw {
  _id: string;
  propertyId: string;
  userId: string; // Only reference to user, no duplicated data
  content: string;
  parentId?: string; // For reply threading
  visibility?: string;
  attachments?: CommentAttachment[];
  images?: CommentImage[];
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

// User data structure for enrichment (from users collection)
export interface CommentUser {
  _id: string;
  firstName: string;
  lastName: string;
  displayName: string; // firstName + lastName
  email?: string;
  role?: string; // Single role (primary system)
  avatar?: string;
  profilePhoto?: string;
  location?: string;
  isOnline?: boolean;
  lastSeen?: string;
}

// Attachment and image interfaces
export interface CommentAttachment {
  _id: string;
  filename: string;
  originalName: string;
  mimetype: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export interface CommentImage {
  _id: string;
  filename: string;
  originalName: string;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  uploadedAt: string;
}

// Enriched comment with user data (what API returns after enrichment)
export interface CommentItem {
  _id: string;
  propertyId: string;
  userId: string;
  content: string;
  parentId?: string;
  visibility?: string;
  attachments?: CommentAttachment[];
  images?: CommentImage[];
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  // Enriched user data (populated by API from users collection)
  user: CommentUser;
  // Reply functionality
  replies?: CommentItem[];
  replyCount?: number;
  // UI state
  isCurrentUser?: boolean;
}

export interface CommentsResponse {
  // New structure that matches backend
  comments?: CommentItem[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  // Legacy structure for backward compatibility
  data?: CommentItem[];
  total?: number;
  page?: number;
  limit?: number;
}

class RealtorCommentsService extends BaseService {
  constructor() {
    super({
      name: "realtor-comments",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 3,
      retryDelay: 1000,
    });
  }

  async list(
    propertyId: string,
    params?: Record<string, any>,
  ): Promise<CommentsResponse> {
    try {
      const res = await this.get<CommentsResponse>(
        `/properties/${propertyId}/comments`,
        params,
      );
      return res;
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      throw error;
    }
  }

  async create(
    propertyId: string,
    payload: {
      content: string;
      parentId?: string; // For replies
      visibility?: string;
      attachments?: File[];
      images?: File[];
      metadata?: any;
      userId?: string; // User ID for comment creation
    },
  ): Promise<CommentItem> {
    try {
      // If there are files (images or attachments), use FormData
      const hasFiles =
        (payload.attachments && payload.attachments.length > 0) ||
        (payload.images && payload.images.length > 0);

      if (hasFiles) {
        const formData = new FormData();

        // Add text fields
        formData.append("content", payload.content);
        if (payload.parentId) {
          formData.append("parentId", payload.parentId);
        }
        if (payload.visibility) {
          formData.append("visibility", payload.visibility);
        }
        if (payload.userId) {
          formData.append("userId", payload.userId);
        }
        if (payload.metadata) {
          formData.append("metadata", JSON.stringify(payload.metadata));
        }

        // Add files
        if (payload.attachments) {
          payload.attachments.forEach((file) => {
            formData.append("files", file);
          });
        }

        if (payload.images) {
          payload.images.forEach((file) => {
            formData.append("files", file);
          });
        }

        // Use fetch directly for FormData to avoid JSON serialization
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/properties/${propertyId}/comments`,
          {
            method: "POST",
            credentials: "include",
            body: formData,
            headers: {
              // Don't set Content-Type - let browser set it with boundary for FormData
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } else {
        // No files, use regular JSON
        const res = await this.post<CommentItem>(
          `/properties/${propertyId}/comments`,
          payload,
        );
        return res;
      }
    } catch (error) {
      console.error("Failed to create comment:", error);
      throw error;
    }
  }

  async update(
    propertyId: string,
    commentId: string,
    payload: Partial<{
      content: string;
      visibility?: string;
      metadata?: any;
    }>,
  ): Promise<CommentItem> {
    const res = await this.put<CommentItem>(
      `/properties/${propertyId}/comments/${commentId}`,
      payload,
    );
    return res;
  }

  async deleteComment(propertyId: string, commentId: string): Promise<void> {
    try {
      await this.delete(`/properties/${propertyId}/comments/${commentId}`);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      throw error;
    }
  }

  async findById(propertyId: string, commentId: string): Promise<CommentItem> {
    const res = await this.get<CommentItem>(
      `/properties/${propertyId}/comments/${commentId}`,
    );
    return res;
  }
}

export const realtorCommentsService = new RealtorCommentsService();
